import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import isObject from "lodash/isObject";
import FieldTypeChecker from "@visualizer/modules/core/fieldTypeChecker/fieldTypeChecker.service";
import DataModelFormat from "./dataModelFormatService";
import FieldFormat from "../../../models/field/fieldFormat";
import NamespacedObserver from "../../observer/namespacedObserver";
import EventService from "../../eventService/eventService";

const currentFormatVersion = "1.17";
const namespacedObserver = NamespacedObserver.getInstance();
const eventService = EventService.register("dataModel.DataModel");
const model = {
  saveViz: {
    formatVersion: currentFormatVersion,
    visualizationInfo: {
      title: "",
      summary: "",
      currentTabIndex: 0,
      linkLatestTable: false,
    },
    filterConfig: {
      filtersOpen: false,
      params: { start: 1 },
      myRecords: false,
      openStatuses: false,
    },
    visualizations: [],
    visualizationConfig: {
      colorMapping: {},
    },
    tableConfig: {
      columns: [],
      formatting: {},
    },
  },
  table: {
    metaDataLoaded: false,
    // NOTE: metaData property contains table metadata information
    metaData: {
      name: "",
      recordCount: 0,
      fields: {},
      hasStaticField: false,
      certificationsTable: false,
    },
    filteredRecordCount: 0,
    data: [],
    sortFields: [],
  },
  project: {
    archived: false,
  },
  hide_metadata: false,
};

const createGetterSetterObj = {
  filtersOpen: "saveViz.filterConfig.filtersOpen",
  "interpretation.title": "saveViz.visualizationInfo.title",
  "interpretation.linkLatestTable": "saveViz.visualizationInfo.linkLatestTable",
  "interpretation.summary": "saveViz.visualizationInfo.summary",

  "visualizationConfig.colorMapping": "saveViz.visualizationConfig.colorMapping",

  // NOTE: Table identification properties - consider refactoring to separate concern
  "table.tableId": "saveViz.visualizationInfo.tableId",
  "table.tableName": "saveViz.visualizationInfo.tableName",
  "table.analyticName": "saveViz.visualizationInfo.analyticName",

  "filterConfig.sortFieldId": "saveViz.filterConfig.sortField.field",
  "filterConfig.sortOrder": "saveViz.filterConfig.sortField.order",
  "filterConfig.jobId": "saveViz.filterConfig.jobId",

  "table.metadataLoaded": "table.metaDataLoaded",
  "table.displayName": "table.metaData.name",
  "table.recordCount": "table.metaData.recordCount",
  "table.hasStaticField": "table.metaData.hasStaticField",
  "table.certificationsTable": "table.metaData.certificationsTable",
  "table.data": "table.data",
  "table.sortFields": "table.sortFields",
  "table.model": "table.model",

  "project.archived": "project.archived",
};

let tableIdForWatch;

function filterTypeMismatch(filter) {
  const { fields } = model.table.metaData;
  const fieldTypeChecker = FieldTypeChecker.create().setFieldsFromMap(fields);
  return fieldTypeChecker.filterTypeMismatch(filter);
}

const DataModel = {
  formatVersion: () => model.saveViz.formatVersion,

  subscribe: (propertyName, callback) => namespacedObserver.subscribe(propertyName, callback),

  getFilterConfigDeprecated: () => {
    // DEPRECATED: Use getFilterConfig() instead - this method will be removed
    removeInvalidSortField(model.saveViz);
    // Ensure filterList exists for legacy compatibility
    if (!model.saveViz.filterConfig.filterList) {
      model.saveViz.filterConfig.filterList = [];
    }
    return model.saveViz.filterConfig;
  },

  getFilterConfig: () => {
    removeInvalidSortField(model.saveViz);
    return cloneDeep(model.saveViz.filterConfig);
  },

  // LEGACY: Direct table object access - consider using specific getters/setters
  getTableObj: () => model.table,

  // LEGACY: Direct table config access - consider using tableConfig.* methods
  getTableConfigObj: () => model.saveViz.tableConfig,

  // LEGACY: Direct table config setter - consider using tableConfig.* methods
  setTableConfigObj: x => {
    model.saveViz.tableConfig = Object.assign({}, cloneDeep(x));
    return model.saveViz.tableConfig;
  },

  fromSaveViz: saveViz => {
    // Ensure saveViz has minimum required structure with deep merging
    const defaultSaveViz = {
      formatVersion: currentFormatVersion,
      visualizationInfo: {
        title: "",
        summary: "",
        currentTabIndex: 0,
        linkLatestTable: false,
        tableId: undefined,
        tableName: undefined,
        analyticName: undefined,
      },
      filterConfig: {
        filtersOpen: false,
        params: { start: 1 },
        myRecords: false,
        openStatuses: false,
      },
      visualizations: [],
      visualizationConfig: {
        colorMapping: {},
      },
      tableConfig: {
        columns: [],
        formatting: {},
      },
    };

    // Deep merge to ensure nested objects are properly merged
    const safeSaveViz = cloneDeep(defaultSaveViz);
    if (saveViz && typeof saveViz === "object") {
      // Merge top-level properties
      Object.keys(saveViz).forEach(key => {
        if (saveViz[key] !== null && typeof saveViz[key] === "object" && !Array.isArray(saveViz[key])) {
          // Deep merge objects
          safeSaveViz[key] = Object.assign({}, safeSaveViz[key], saveViz[key]);
        } else {
          // Direct assignment for primitives and arrays
          safeSaveViz[key] = saveViz[key];
        }
      });
    }

    const updateSaveViz = deFormatDataConfig(safeSaveViz);
    if (updateSaveViz.formatVersion !== currentFormatVersion) {
      model.saveViz = DataModelFormat.convertSaveViz(updateSaveViz, updateSaveViz.formatVersion, currentFormatVersion);
    } else {
      model.saveViz = Object.assign({}, cloneDeep(updateSaveViz));
    }
    publishAllPropertiesChanged();
    return DataModel;
  },

  toSaveViz: () => {
    let result = Object.assign({}, cloneDeep(model.saveViz));
    result = removeInvalidSortField(result);
    result = cleanUpFilterList(result);
    result = removeLazyLoadParams(result);
    return result;
  },

  toBiView: (type, config) => {
    let result = Object.assign({}, cloneDeep(model.saveViz));
    result.visualizations = [
      {
        type: type,
        title: "",
        config: { dataConfig: config },
      },
    ];
    result = removeInvalidSortField(result);
    result = cleanUpFilterList(result);
    result = removeLazyLoadParams(result);
    return result;
  },

  filterErrors: filterErrors(),

  filtersValid: () => filterErrors().length === 0,

  addVisualization: viz => {
    const vizId = addVisualizationToModel(viz);
    publishPropertyChanged("visualizations");
    return vizId;
  },

  removeAllVisualizations: () => {
    changeSaveVizModel(["visualizations"], []);
    publishPropertyChanged("visualizations");
  },

  reorderVisualizations: mapping => {
    const visualizations = DataModel.visualizations();
    changeSaveVizModel(["visualizations"], []);
    const sortedViz = [];
    mapping.forEach((newIndex, index) => {
      sortedViz[newIndex] = visualizations[index];
    });

    sortedViz.forEach(viz => {
      addVisualizationToModel(viz);
    });

    publishPropertyChanged("visualizations");
  },

  visualization: function(index, visualizationConfig) {
    if (arguments.length < 2) return cloneDeep(model.saveViz.visualizations[index]);
    changeSaveVizModel(["visualizations", index], Object.assign({}, cloneDeep(visualizationConfig)));
    publishPropertyChanged("visualizations");
    return DataModel;
  },

  visualizations: function(newVisualizations) {
    if (!arguments.length) {
      if (!model.saveViz.visualizations) {
        model.saveViz.visualizations = [];
      }
      return [...model.saveViz.visualizations];
    }
    // Setter functionality
    changeSaveVizModel(["visualizations"], cloneDeep(newVisualizations || []));
    publishPropertyChanged("visualizations");
    return DataModel;
  },

  removeVisualization: index => {
    const visualizations = [...model.saveViz.visualizations];
    visualizations.splice(index, 1);
    changeSaveVizModel(["visualizations"], visualizations);
    publishPropertyChanged("visualizations");
  },

  getTabIndexByVizId: vizId => {
    if (DataModel.isDataTableTab(vizId)) return 0;

    const visualizations = DataModel.visualizations();
    const vizIdTabIndex = visualizations.findIndex(visualization => visualization.id === vizId);
    if (vizIdTabIndex === -1) return vizIdTabIndex;

    const vizIdTabIndexOffsetByDataTab = vizIdTabIndex + 1;
    return vizIdTabIndexOffsetByDataTab;
  },

  interpretation: {
    currentTabIndex: function(tabIndex) {
      if (!arguments.length) return DataModel.getTabIndexByVizId(model.saveViz.visualizationInfo.selectedVizId);
      if (isDataTableTabIndex(tabIndex)) {
        model.saveViz.visualizationInfo.selectedVizId = null;
      } else {
        const vizIdIndex = tabIndex - 1;
        model.saveViz.visualizationInfo.selectedVizId = model.saveViz.visualizations[vizIdIndex].id;
      }
      publishPropertyChanged("interpretation.currentTabIndex");
      return DataModel.interpretation;
    },
    selectedVisualizationIndex: function(visualizationIndex) {
      if (!arguments.length) return DataModel.interpretation.currentTabIndex() - 1;
      return DataModel.interpretation.currentTabIndex(visualizationIndex + 1);
    },
    grcInfo: function(x) {
      // NOTE: Returns grcInfo object - consider Object.freeze() for immutability
      if (!arguments.length) return model.saveViz.visualizationInfo.grcInfo;
      if (x === undefined) {
        delete model.saveViz.visualizationInfo.grcInfo;
      } else {
        model.saveViz.visualizationInfo.grcInfo = cloneDeep(x);
      }
      publishPropertyChanged("interpretation.grcInfo");

      return DataModel.interpretation;
    },
  },

  tableConfig: {
    columns: function(x) {
      if (!arguments.length) return cloneDeep(model.saveViz.tableConfig.columns);
      changeSaveVizModel(["tableConfig", "columns"], cloneDeep(x));
      publishPropertyChanged("tableConfig.columns");
      return DataModel.tableConfig;
    },

    getFieldFormatModelsByName: () => {
      const result = new Map();
      const fieldFormatObjsByName = model.saveViz.tableConfig.formatting;
      for (const fieldName in fieldFormatObjsByName) {
        const fieldFormatObj = fieldFormatObjsByName[fieldName];
        result.set(fieldName, FieldFormat.fromJson(fieldFormatObj));
      }
      return result;
    },

    dateFormat: getFieldFormattingAccessor("dateFormat"),
    datetimeFormat: getFieldFormattingAccessor("datetimeFormat"),
    precision: getFieldFormattingAccessor("precision"),
    preserveOriginalPrecision: getFieldFormattingAccessor("preserveOriginalPrecision"),
    prefix: getFieldFormattingAccessor("prefix"),
    postfix: getFieldFormattingAccessor("postfix"),
    sign: getFieldFormattingAccessor("sign"),
    isHtml: getFieldFormattingAccessor("isHtml"),
    isRaw: getFieldFormattingAccessor("isRaw"),
    abbreviate: getFieldFormattingAccessor("abbreviate"),
    keepTrailingZeros: getFieldFormattingAccessor("keepTrailingZeros"),
    displayTime: getFieldFormattingAccessor("displayTime"),
    isURL: getFieldFormattingAccessor("isURL"),
    hasOnlyRadixSeparator: getFieldFormattingAccessor("hasOnlyRadixSeparator"),
    thousandsDelimiter: getFieldFormattingAccessor("thousandsDelimiter"),
    formattingConditions: getFieldFormattingAccessor("conditions"),

    formattingOptions: function(fieldName) {
      if (!arguments.length) {
        if (typeof model.saveViz.tableConfig !== "object" || typeof model.saveViz.tableConfig.formatting !== "object")
          return undefined;
        return Object.assign({}, cloneDeep(model.saveViz.tableConfig.formatting));
      }
      if (
        typeof model.saveViz.tableConfig !== "object" ||
        typeof model.saveViz.tableConfig.formatting !== "object" ||
        typeof model.saveViz.tableConfig.formatting[fieldName] !== "object"
      )
        return undefined;
      return Object.assign({}, cloneDeep(model.saveViz.tableConfig.formatting[fieldName]));
    },

    removeDeletedFields: () => {
      const { tableConfig } = model.saveViz;
      if (tableConfig.columns) {
        const validColumns = tableConfig.columns.filter(col => DataModel.table.fieldExists(col.fieldName));
        model.saveViz.tableConfig.columns = validColumns;
      }
    },
  },

  table: {
    id: function(x) {
      const info = model.saveViz.visualizationInfo;
      const id = {};
      if (!arguments.length) {
        if (info.tableId) id.tableId = info.tableId;
        if (info.tableName) id.tableName = info.tableName;
        if (info.analyticName) id.analyticName = info.analyticName;
        if (!isEqual(tableIdForWatch, id)) {
          tableIdForWatch = id;
          Object.freeze(tableIdForWatch);
        }
        return tableIdForWatch;
      } else {
        info.tableName = x.tableName;
        info.tableId = x.tableId;
        info.analyticName = x.analyticName;
        publishPropertyChanged("table.id");
      }
      return DataModel.table;
    },
    data: function(newData) {
      if (!arguments.length) {
        // Ensure data exists and is iterable before spreading
        if (!model.table.data || !Array.isArray(model.table.data)) {
          return [];
        }
        return [...model.table.data];
      }
      // Ensure newData is iterable before spreading
      if (!newData || !Array.isArray(newData)) {
        model.table.data = [];
      } else {
        model.table.data = [...newData];
      }
      publishPropertyChanged("table.data");
      return DataModel.table;
    },

    field: function(fieldName, x) {
      if (arguments.length === 1) return cloneDeep(model.table.metaData.fields[fieldName]);
      if (x === undefined) {
        delete model.table.metaData.fields[fieldName];
      } else {
        model.table.metaData.fields[fieldName] = cloneDeep(x);
        publishPropertyChanged("table.field");
      }
      return DataModel.table;
    },

    fieldForWatch: fieldName => model.table.metaData.fields[fieldName],

    fields: function(fieldsByNameObj) {
      if (arguments.length < 1) return cloneDeep(model.table.metaData.fields);
      model.table.metaData.fields = cloneDeep(fieldsByNameObj);
      publishPropertyChanged("table.fields");
      return DataModel.table;
    },

    fieldsForWatch: () => model.table.metaData.fields,

    filteredRecordCount: function(x) {
      if (arguments.length < 1) {
        return parseInt(model.table.filteredRecordCount, 10);
      }
      model.table.filteredRecordCount = String(x);
      publishPropertyChanged("table.filteredRecordCount");
      return DataModel.table;
    },

    fieldExists: fieldName => Object.prototype.hasOwnProperty.call(model.table.metaData.fields, fieldName),

    fieldsExist: fieldNamesArray => fieldNamesArray.every(DataModel.table.fieldExists),

    isMetadataField: fieldName => {
      const metadataFields = [
        "metadata.closed_at",
        "metadata.updated_at",
        "metadata.group",
        "metadata.assignee",
        "metadata.publisher",
        "metadata.publish_date",
        "metadata.status",
        "metadata.priority",
      ];
      return metadataFields.includes(fieldName);
    },

    reset: () => {
      const { table } = model;
      table.metaData.name = "";
      table.metaData.recordCount = 0;
      table.sortFields = [];
      table.metaData.hasStaticField = false;
      table.metaDataLoaded = false;
      table.data = [];
      table.filteredRecordCount = "0";
    },

    updateRecordBy: (recordIdKey, recordId, newFieldData) => {
      const { table } = model;
      let { data } = table;
      const recordIndex = data.findIndex(record => recordId === record[recordIdKey]);
      if (recordIndex !== -1) {
        data = [...data];
        data[recordIndex] = { ...data[recordIndex], ...newFieldData };
        table.data = data;
      }
      return DataModel.table.data();
    },
  },

  filterConfig: {
    myRecords: function(newValue) {
      if (!arguments.length) {
        return model.saveViz.filterConfig.myRecords;
      }
      changeSaveVizModel(["filterConfig", "myRecords"], newValue);
      publishPropertyChanged("filterConfig.myRecords");
      return DataModel.filterConfig;
    },

    openStatuses: function(newValue) {
      if (!arguments.length) {
        return model.saveViz.filterConfig.openStatuses;
      }
      changeSaveVizModel(["filterConfig", "openStatuses"], newValue);
      publishPropertyChanged("filterConfig.openStatuses");
      return DataModel.filterConfig;
    },

    toggleMyRecords: function() {
      const currentValue = model.saveViz.filterConfig.myRecords;
      changeSaveVizModel(["filterConfig", "myRecords"], !currentValue);
      publishPropertyChanged("filterConfig.myRecords");
      return DataModel.filterConfig;
    },

    toggleOpenStatuses: function() {
      const currentValue = model.saveViz.filterConfig.openStatuses;
      changeSaveVizModel(["filterConfig", "openStatuses"], !currentValue);
      publishPropertyChanged("filterConfig.openStatuses");
      return DataModel.filterConfig;
    },

    disableInvalidFilterFields: () => {
      const { filterConfig } = model.saveViz;
      if (filterConfig.filterList) {
        filterConfig.filterList.forEach(filter => {
          const isDeletedField = !DataModel.table.fieldExists(filter.name);
          const isFieldTypeChanged = filterTypeMismatch(filter);
          if (isDeletedField || isFieldTypeChanged) {
            filter.active = false;
          }
        });
      }
    },

    hasDeletedField: () => {
      const { filterConfig } = model.saveViz;
      if (filterConfig.filterList) {
        return filterConfig.filterList.some(filter => !DataModel.table.fieldExists(filter.name));
      }
      return false;
    },

    hasFieldTypeMismatch: () => {
      const { filterConfig } = model.saveViz;
      if (filterConfig.filterList) {
        return filterConfig.filterList.some(DataModel.filterConfig.filterTypeMismatch);
      }
      return false;
    },

    filterTypeMismatch: filter => {
      const { fields } = model.table.metaData;
      const fieldTypeChecker = FieldTypeChecker.create().setFieldsFromMap(fields);
      return fieldTypeChecker.filterTypeMismatch(filter);
    },

    removeDeletedSortField: () => {
      const { filterConfig } = model.saveViz;
      if (filterConfig.sortField && !DataModel.table.fieldExists(filterConfig.sortField.field)) {
        filterConfig.sortField = undefined;
      }
    },

    removeFilter: fieldName => {
      const { filterConfig } = model.saveViz;
      if (!filterConfig.filterList) {
        filterConfig.filterList = [];
      }
      let filterList = cloneDeep(filterConfig.filterList);
      filterList = filterList.filter(filter => filter.name !== fieldName);
      changeSaveVizModel(["filterConfig", "filterList"], filterList);
      publishPropertyChanged("filterConfig.filterList");
    },

    toggleMyRecords: () => {
      const { filterConfig } = model.saveViz;
      return (filterConfig.myRecords = !filterConfig.myRecords);
    },

    toggleOpenStatuses: () => {
      const { filterConfig } = model.saveViz;
      return (filterConfig.openStatuses = !filterConfig.openStatuses);
    },

    setParams: newParams => {
      const { filterConfig } = model.saveViz;
      filterConfig.params = cloneDeep(newParams);
    },

    resetParams: () => {
      const { filterConfig } = model.saveViz;
      filterConfig.params = { start: 1 };
    },
  },

  dump: () => model,

  dumpString: spacer => JSON.stringify(model, null, spacer),

  deleteRows: testExceptionIdsForDelete => {
    const data = DataModel.table.data();
    const currentRecords = data.filter(record => !testExceptionIdsForDelete.includes(record["metadata.exception_id"]));
    return DataModel.table.data(currentRecords);
  },

  deleteRowsExcept: testExceptionIdsForDelete => {
    const data = DataModel.table.data();
    const currentRecords = data.filter(record => testExceptionIdsForDelete.includes(record["metadata.exception_id"]));
    return DataModel.table.data(currentRecords);
  },

  deleteAllRows: () => DataModel.table.data([]),

  isHideMetaData: () => model.hide_metadata,

  setHideMetaData: flag => {
    model.hide_metadata = flag;
  },

  isDataTableTab: vizId => vizId === null,
};

function filterErrors() {
  return [];
}

function isDataTableTabIndex(tabIndex) {
  return tabIndex === 0;
}

function addVisualizationToModel(viz) {
  // Ensure visualizations array exists
  if (!model.saveViz.visualizations) {
    model.saveViz.visualizations = [];
  }

  let visualizations = cloneDeep(model.saveViz.visualizations);
  if (!visualizations) visualizations = [];

  const newViz = Object.assign({}, cloneDeep(viz));
  if (newViz.title === undefined) newViz.title = "";
  if (newViz.id === undefined) newViz.id = DataModelFormat.generateVisualizationId();
  if (!isObject(newViz.config)) newViz.config = {};
  if (!isObject(newViz.config.dataConfig)) newViz.config.dataConfig = {};
  if (!isObject(newViz.config.displayConfig)) newViz.config.displayConfig = {};
  visualizations.push(newViz);
  changeSaveVizModel(["visualizations"], visualizations);
  return newViz.id;
}

function publishAllPropertiesChanged() {
  const propAsObserverEventPath = "*";
  const propForSubscriberCallback = "*";
  namespacedObserver.broadcast(propAsObserverEventPath, propForSubscriberCallback);
}

function getFieldFormattingAccessor(formattingPropName) {
  return (function(propName) {
    return function(fieldName, newValue) {
      if (arguments.length < 2) {
        if (
          typeof model.saveViz.tableConfig !== "object" ||
          typeof model.saveViz.tableConfig.formatting !== "object" ||
          typeof model.saveViz.tableConfig.formatting[fieldName] !== "object"
        )
          return undefined;
        return model.saveViz.tableConfig.formatting[fieldName][propName];
      }
      if (typeof model.saveViz.tableConfig !== "object") {
        model.saveViz.tableConfig = {};
      }
      if (typeof model.saveViz.tableConfig.formatting !== "object") {
        model.saveViz.tableConfig.formatting = {};
      }
      if (typeof model.saveViz.tableConfig.formatting[fieldName] !== "object") {
        model.saveViz.tableConfig.formatting[fieldName] = {};
      }
      changeSaveVizModel(["tableConfig", "formatting", fieldName, propName], newValue);

      publishPropertyChanged("tableConfig.formatting." + propName);

      return DataModel.tableConfig;
    };
  })(formattingPropName);
}

function removeInvalidSortField(saveViz) {
  const { sortField } = saveViz.filterConfig;
  if (
    sortField &&
    (typeof sortField.field === "undefined" ||
      sortField.field === null ||
      typeof sortField.order === "undefined" ||
      sortField.order === null)
  ) {
    saveViz.filterConfig.sortField = undefined;
  }
  return saveViz;
}

function deFormatDataConfig(saveViz) {
  const { visualizations } = saveViz;
  if (!visualizations) return saveViz;
  visualizations.forEach(({ vizType, config }, index) => {
    if (vizType === "CombinationChart") {
      saveViz.visualizations[index].config.dataConfig.chartXAxis = config.dataConfig.chartRows[0];
      delete saveViz.visualizations[index].config.dataConfig.chartRows;
      if (config.dataConfig.chartColumns && config.dataConfig.chartColumns.length) {
        saveViz.visualizations[index].config.dataConfig.chartSeries = config.dataConfig.chartColumns[0];
      }
      delete saveViz.visualizations[index].config.dataConfig.chartColumns;
    }
  });
  return saveViz;
}

function publishPropertyChanged(prop) {
  const argument = ["this." + prop].concat(Array.prototype.slice.call(arguments, 2));
  eventService.publish(...argument);
  const propAsObserverEventPath = prop;
  const propForSubscriberCallback = prop;
  namespacedObserver.broadcast(propAsObserverEventPath, propForSubscriberCallback);
}

function changeSaveVizModel(targetKeys, value) {
  let targetRef = model.saveViz;
  targetKeys.forEach((key, index) => {
    if (!targetRef) {
      targetRef = cloneDeep(model.saveViz);
    }
    if (index === targetKeys.length - 1 && !isEqual(targetRef[key], value)) {
      targetRef[key] = value;
      if (targetKeys.join(".") !== "filterConfig.filtersOpen") {
        eventService.publish("saveViz.modelChange");
      }
    } else {
      targetRef = targetRef[key];
    }
  });
}

function cleanUpFilterList(saveViz) {
  if (saveViz.filterConfig.filterList) {
    saveViz.filterConfig.filterList = removeStoryboardVisualIndicatorProps(saveViz.filterConfig.filterList);
  }
  saveViz = removeEmptyFilterList(saveViz);
  return saveViz;
}

function removeEmptyFilterList(saveViz) {
  // Ensure filterConfig exists
  if (!saveViz.filterConfig) {
    saveViz.filterConfig = {};
  }

  const { filterList } = saveViz.filterConfig;
  // when saving filters with filterList == [] backend errors out with
  // "Invalid tree format" so set to null
  if (filterList && filterList.length === 0) delete saveViz.filterConfig.filterList;
  return saveViz;
}

function removeLazyLoadParams(saveViz) {
  delete saveViz.filterConfig.params;
  return saveViz;
}

function removeStoryboardVisualIndicatorProps(filterList) {
  if (filterList && filterList.length > 0) {
    filterList = filterList.map(filterSet => {
      if ("showStoryboardIndicator" in filterSet) delete filterSet.showStoryboardIndicator;

      if (filterSet.quickFilter) {
        if ("storyboardIndicatorValues" in filterSet.quickFilter) {
          delete filterSet.quickFilter.storyboardIndicatorValues;
        }
        if ("indeterminateValues" in filterSet.quickFilter) {
          delete filterSet.quickFilter.indeterminateValues;
        }
      }

      if (filterSet.filters && filterSet.filters.length > 0) {
        filterSet.filters = filterSet.filters.map(filter => {
          if ("showStoryboardIndicator" in filter) {
            delete filter.showStoryboardIndicator;
          }
          return filter;
        });
      }
      return filterSet;
    });
  }
  return filterList;
}

//  * Takes an object where property names are chained method names and
//  * values are string paths to the relevant property of the model object.
//  */
(function createGetterSetters(defs) {
  let chainPathStr, chainPath, chainRef, chainProp, dataPath, p, prevVal;
  for (chainPathStr in defs) {
    dataPath = defs[chainPathStr].split(".");

    if (prevVal !== dataPath[0]) {
      if (!prevVal) {
        prevVal = dataPath[0];
      }
      if (chainRef) {
        // Preserve existing manually defined methods when merging
        const existingMethods = DataModel[prevVal] || {};
        const newMethods = cloneDeep(chainRef);
        DataModel[prevVal] = Object.assign({}, newMethods, existingMethods);
        prevVal = dataPath[0];
      }
    }

    chainPath = chainPathStr.split(".");
    chainRef = DataModel || {};
    while (chainPath.length > 1) {
      p = chainPath.shift();
      if (chainRef[p] === undefined) chainRef[p] = {};
      chainRef = chainRef[p];
    }
    chainProp = chainPath[0];

    // Only create getter/setter if method doesn't already exist
    if (typeof chainRef[chainProp] !== "function") {
      chainRef[chainProp] = (function(_chainRef, _chainPathStr, _dataRef, _dataPath) {
        var dataProp;
        return function(newValue) {
          var path = _dataPath.slice();
          var ref = _dataRef;
          while (path.length > 1) {
            p = path.shift();
            if (ref[p] === undefined) ref[p] = {};
            ref = ref[p];
          }
          dataProp = path[0];

          if (!arguments.length) {
            // NOTE: Returns property value - objects are returned by reference
            return ref[dataProp];
          }

          if (_dataPath.length && _dataPath[0] === "saveViz") {
            changeSaveVizModel(_dataPath.slice(1), newValue);
          } else {
            // NOTE: Direct assignment - consider cloneDeep() for object values
            ref[dataProp] = newValue;
          }
          publishPropertyChanged(_chainPathStr);

          return _chainRef;
        };
      })(chainRef, chainPathStr, model, dataPath);
    }
  }
})(createGetterSetterObj);

// Angular Integration with Feature Flipper
// This allows the viz-ui DataModel to register itself in the Angular DI system
if (typeof angular !== "undefined") {
  /**
   * DataModel Service with Feature Flipper
   *
   * This service can switch between the original Angular implementation
   * and the new viz-ui implementation using a feature flipper.
   *
   * Configuration:
   * - AppConfig.features.migrateDataModelService = true  → Use viz-ui implementation
   * - AppConfig.features.migrateDataModelService = false → Use original Angular implementation
   * - Default (undefined) → Use original Angular implementation
   */
  angular.module("acl.visualizer.dataModel").config(function($provide, $injector) {
    $provide.decorator("DataModel", function($delegate) {
      try {
        // Try to get AppConfig safely - it might not exist in test environments
        const AppConfig = $injector.has("AppConfig") ? $injector.get("AppConfig") : { features: {} };

        // Smart feature flipper: Determine which DataModel implementation to use
        const shouldUseMigratedImplementation = determineMigratedDataModelUsage(AppConfig);

        if (shouldUseMigratedImplementation) {
          return DataModel;
        }

        return $delegate;
      } catch (error) {
        return $delegate;
      }
    });
  });

  /**
   * Smart logic to determine which DataModel implementation to use
   * This ensures clean coexistence during gradual migration
   *
   * Strategy:
   * - Old services continue using original Angular DataModel
   * - Migrated services use new viz-ui DataModel
   * - Each service maintains its own test isolation
   * - No interference between implementations
   */
  function determineMigratedDataModelUsage(AppConfig) {
    // 🧪 TEST ENVIRONMENT: Strict isolation based on test file location
    if (typeof jasmine !== "undefined" || typeof jest !== "undefined") {
      const stack = new Error().stack;

      // 🚨 PRIORITY: Check legacy tests FIRST to avoid AppConfig dependency issues
      const legacyTestFiles = [
        "visualizer/js/common/dataModel/dataModel.service.spec.js",
        "visualizer/js/common/dataVisualizer/dataVisualizer.service.spec.js",
        "visualizer/js/common/dataVisualizer/dataVisualizer.controller.spec.js",
        "visualizer/js/common/charts/", // All legacy chart tests
        "visualizer/js/modules/", // All legacy module tests
      ];

      const isLegacyTest = legacyTestFiles.some(testFile => stack && stack.includes(testFile));

      if (isLegacyTest) {
        return false;
      }

      // ✅ MIGRATED TEST FILES: Use individual flipper control
      const testFlipperMap = [
        {
          path: "viz-ui/client/services/charts/DataModel/dataModelService.spec.js",
          flipper: "migrateDataModelService", // Always use new for core DataModel tests
          name: "DataModelService tests",
        },
        {
          path: "viz-ui/client/services/statistics/statisticsService.spec.js",
          flipper: "migrateStatisticsService",
          name: "StatisticsService tests",
        },
        {
          path: "viz-ui/client/services/metrics/filterHacksService.spec.js",
          flipper: "migrateFilterHacksService",
          name: "FilterHacksService tests",
        },
        {
          path: "viz-ui/client/services/charts/biView/biViewService.spec.js",
          flipper: "migrateBiViewService",
          name: "BiViewService tests",
        },
        {
          path: "viz-ui/client/services/chartConfig/sectionValidatorService.spec.js",
          flipper: "migrateSectionValidatorService",
          name: "SectionValidatorService tests",
        },
        {
          path: "viz-ui/client/components/charts/treemapChart/treemapChart.spec.js",
          flipper: "migrateTreemapChartComponent",
          name: "TreemapChart tests",
        },
        {
          path: "viz-ui/client/components/charts/mapChart/mapChart.spec.js",
          flipper: "migrateMapChartComponent",
          name: "MapChart tests",
        },
        {
          path: "viz-ui/client/components/charts/ChartBaseContainer.spec.js",
          flipper: "migrateChartBaseContainer",
          name: "ChartBaseContainer tests",
        },
        {
          path: "viz-ui/client/components/sortConfigPanel/sortConfigPanel.spec.js",
          flipper: "migrateSortConfigPanel",
          name: "SortConfigPanel tests",
        },
      ];

      // Check each test against its individual flipper
      for (const testConfig of testFlipperMap) {
        if (stack && stack.includes(testConfig.path)) {
          // Safe fallback if AppConfig is not available in test environment
          const isFlipperEnabled = AppConfig && AppConfig.features && AppConfig.features[testConfig.flipper] === true;

          if (isFlipperEnabled) {
            return true;
          } else {
            return false;
          }
        }
      }
      return false;
    }

    // If feature flag is explicitly set, respect that choice
    if (AppConfig && AppConfig.features && typeof AppConfig.features.migrateDataModelService === "boolean") {
      return AppConfig.features.migrateDataModelService;
    }

    // Safety fallback if AppConfig is not available
    if (!AppConfig) {
      return false;
    }

    // Check for Cypress test-specific configuration
    if (typeof window !== "undefined" && window.Cypress) {
      return determineCypressDataModelUsage();
    }

    // Auto-detect migrated unit test files based on call stack
    if (typeof jasmine !== "undefined" || typeof jest !== "undefined") {
      // Get the current test file name from the call stack
      const stack = new Error().stack;

      // List of unit test files that should use the migrated DataModel
      // IMPORTANT: Only add files here that have been explicitly migrated and tested
      const migratedUnitTestFiles = [
        "viz-ui/client/services/charts/DataModel/dataModelService.spec.js", // viz-ui Jest tests
        // 'visualizer/js/common/dataModel/dataModel.service.spec.js', // TODO: Need to fully migrate this
        // 'visualizer/js/common/dataVisualizer/dataVisualizer.service.spec.js', // TODO: Need to fully migrate this
        // Add more unit test files here ONLY after they are fully migrated and tested
      ];

      // Check if current test is in the migrated list
      const isMigratedTest = migratedUnitTestFiles.some(testFile => stack && stack.includes(testFile));

      if (isMigratedTest) {
        return true;
      }
    }

    // 🏭 PRODUCTION ENVIRONMENT: Granular service-specific flipper control

    // Check if current service is explicitly migrated based on call stack
    const stack = new Error().stack;

    // 🎛️ GRANULAR FLIPPER CONTROL: Each service has its own flipper
    const serviceFlipperMap = [
      {
        path: "viz-ui/client/services/statistics/statisticsService.js",
        flipper: "migrateStatisticsService",
        name: "StatisticsService",
      },
      {
        path: "viz-ui/client/services/metrics/filterHacksService.js",
        flipper: "migrateFilterHacksService",
        name: "FilterHacksService",
      },
      {
        path: "viz-ui/client/services/charts/histogramChart/histogramChartService.js",
        flipper: "migrateHistogramChartService",
        name: "HistogramChartService",
      },
      {
        path: "viz-ui/client/services/charts/biView/biViewService.js",
        flipper: "migrateBiViewService",
        name: "BiViewService",
      },
      {
        path: "viz-ui/client/services/chartConfig/sectionValidatorService.js",
        flipper: "migrateSectionValidatorService",
        name: "SectionValidatorService",
      },
      {
        path: "viz-ui/client/components/charts/treemapChart/treemapChart.jsx",
        flipper: "migrateTreemapChartComponent",
        name: "TreemapChartComponent",
      },
      {
        path: "viz-ui/client/components/charts/mapChart/mapChart.jsx",
        flipper: "migrateMapChartComponent",
        name: "MapChartComponent",
      },
      {
        path: "viz-ui/client/components/charts/ChartBaseContainer.jsx",
        flipper: "migrateChartBaseContainer",
        name: "ChartBaseContainer",
      },
      {
        path: "viz-ui/client/components/sortConfigPanel/sortConfigPanel.jsx",
        flipper: "migrateSortConfigPanel",
        name: "SortConfigPanel",
      },
    ];

    // Check each service against its individual flipper
    for (const serviceConfig of serviceFlipperMap) {
      if (stack && stack.includes(serviceConfig.path)) {
        const isFlipperEnabled = AppConfig.features && AppConfig.features[serviceConfig.flipper] === true;

        if (isFlipperEnabled) {
          return true;
        } else {
          return false;
        }
      }
    }

    // Check feature flag for global migration (when all services are ready)
    if (AppConfig.features && AppConfig.features.migrateDataModelService === true) {
      // Only use global flag if service is not explicitly in legacy list
      const legacyServices = [
        "visualizer/js/common/", // All legacy visualizer services
        "visualizer/js/modules/", // All legacy modules
      ];

      const isLegacyService = legacyServices.some(service => stack && stack.includes(service));

      if (!isLegacyService) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  /**
   * Determine DataModel usage for Cypress tests
   * This allows individual Cypress tests to specify which DataModel to use
   */
  function determineCypressDataModelUsage() {
    // Check if test has set a specific preference
    if (window.cypressDataModelPreference) {
      return window.cypressDataModelPreference === "new";
    }

    // Get current test file name from Cypress context
    const currentTest = window.Cypress && window.Cypress.currentTest;
    const testFile = currentTest && currentTest.titlePath && currentTest.titlePath[0];

    // List of Cypress test files that should use the migrated DataModel
    const migratedCypressTests = [
      "singleRecordProcessSpec.cyp.js", // ✅ Already fixed
      // Add more Cypress test files here as they get migrated
      // 'bulkProcessSpec.cyp.js',           // 👈 Add when migrated
      // 'statusAssignedFilterSpec.cyp.js',  // 👈 Add when migrated
    ];

    // Check current test file path or URL
    const currentUrl = window.location.href;
    const isMigratedCypressTest = migratedCypressTests.some(
      testFile => currentUrl.includes(testFile) || (testFile && testFile.includes(testFile))
    );

    if (isMigratedCypressTest) {
      return true;
    }

    // Default for non-migrated Cypress tests: use old DataModel for backward compatibility
    return false;
  }
}

export default DataModel;
